import React, { useState } from 'react';
import { Box, Typography, Paper, Stack, Tab, Tabs, styled } from '@mui/material';
import HoverableStockRow from './HoverableStockRow';
import { TableRowSkeleton } from '../../../components/DashboardSkeletons';

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: '40px',
  marginBottom: theme.spacing(1),
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.85rem',
  minHeight: '40px',
  minWidth: '80px',
  padding: '6px 12px',
  marginRight: theme.spacing(1),
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.text.primary,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    minWidth: '70px',
    marginRight: theme.spacing(0.5),
  },
}));

const ExchangeBadge = ({ exchange }) => {
  const isNSE = (exchange || 'NSE').toUpperCase().includes('NSE');
  return (
    <Box sx={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      bgcolor: isNSE ? '#f5f5f5' : '#fff3e0',
      color: isNSE ? '#666' : '#e65100',
      px: 0.8, 
      py: 0.2, 
      borderRadius: '4px',
      fontSize: '0.6rem',
      fontWeight: 800,
      ml: 1,
      border: '1px solid #eee'
    }}>
      {isNSE ? 'NSE' : 'BSE'}
    </Box>
  );
};

const StockRow = ({ stock, type }) => {
  const ltp = parseFloat(stock.ltp || stock.last_price || stock.close || 0);
  const closePrice = parseFloat(stock.close_price || stock.prev_close || stock.previous_close || stock.prevClose || stock.prev_close_price || 0);
  const absChange = ltp - closePrice;
  const pctChange = closePrice === 0 ? 0 : (absChange / closePrice) * 100;
  const isUp = pctChange >= 0;
  
  const yearlyVal = type === 'high' ? (stock.yearly_high || stock.high52) : (stock.yearly_low || stock.low52);
  const fullName = stock.company_name || stock.symbol_name || stock.display_name || '';
  const symbol = stock.trading_symbol || stock.symbol || 'UNKNOWN';

  return (
    <HoverableStockRow stock={stock}>
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      py: { xs: 1.2, sm: 1.8 }, 
      borderBottom: '1px solid #f0f0f0',
      '&:last-child': { borderBottom: 'none' }
    }}>
      {/* Column 1: Scrip Name */}
      <Box sx={{ flex: { xs: 1.5, sm: 2 }, minWidth: 0, pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
            {symbol}
          </Typography>
          <ExchangeBadge exchange={stock.exchange} />
        </Box>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            display: 'block', 
            fontSize: '0.65rem', 
            fontWeight: 600, 
            mt: 0.2, 
            textTransform: 'uppercase', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap' 
          }}
        >
          {fullName}
        </Typography>
      </Box>

      {/* Column 2: 52W Price - Hidden on very small screens */}
      <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }}>
        <Typography variant="subtitle2" fontWeight="700" sx={{ fontSize: '0.85rem' }}>
          ₹{parseFloat(yearlyVal || ltp).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      </Box>

      {/* Column 3: LTP - Stacked on Mobile */}
      <Box sx={{ flex: 1, textAlign: 'right', pr: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'block' } }}>
        <Typography variant="subtitle2" fontWeight="700" sx={{ fontSize: '0.9rem' }}>
          ₹{ltp.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      </Box>

      {/* Column 4: Change Stack (On mobile this column also contains LTP and 52W optionally) */}
      <Box sx={{ flex: { xs: 1, sm: 1.5 }, textAlign: 'right' }}>
        {/* LTP shown only on Mobile here */}
        <Typography variant="subtitle2" fontWeight="800" sx={{ display: { xs: 'block', sm: 'none' }, fontSize: '0.85rem' }}>
          ₹{ltp.toFixed(2)}
        </Typography>
        
        <Typography variant="subtitle2" fontWeight="600" color={isUp ? '#2e7d32' : '#d32f2f'} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
          {isUp ? '' : '-'}{Math.abs(absChange).toFixed(2)}
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' }, ml: 0.5 }}>
            ({isUp ? '+' : ''}{pctChange.toFixed(2)}%)
          </Box>
        </Typography>
        
        <Typography variant="caption" fontWeight="600" color={isUp ? '#2e7d32' : '#d32f2f'} sx={{ fontSize: '0.7rem', opacity: 0.9, display: { xs: 'none', sm: 'block' } }}>
          {isUp ? '+' : ''}{pctChange.toFixed(2)}%
        </Typography>
      </Box>
    </Box>
    </HoverableStockRow>
  );
};

const YearlyHighLowWidget = ({ loading, highData, lowData }) => {
  const [tabValue, setTabValue] = useState(0);

  if (loading) {
    return (
      <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 4, border: '1px solid #eee', height: '100%', minHeight: '415px' }} elevation={0}>
        <Typography variant="h6" fontWeight="800" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          Yearly Performance
        </Typography>
        <TableRowSkeleton count={5} />
      </Paper>
    );
  }

  const currentData = tabValue === 0 ? highData : lowData;
  const displayData = Array.isArray(currentData) ? currentData.slice(0, 5) : [];

  return (
    <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 4, border: '1px solid #eee', height: '100%', minHeight: '415px' }} elevation={0}>
      <Typography variant="h6" fontWeight="800" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
        Yearly Performance
      </Typography>

      <StyledTabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
        <StyledTab label="52W HIGH" />
        <StyledTab label="52W LOW" />
      </StyledTabs>

      {/* Table Header */}
      <Box sx={(theme) => ({ display: 'flex', px: 0.5, py: 1.2, borderBottom: `2px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.default, mx: -0.5 })}>
        <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ flex: { xs: 1.5, sm: 2 }, fontSize: '0.7rem' }}>SCRIP</Typography>
        <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ flex: 1, fontSize: '0.7rem', display: { xs: 'none', sm: 'block' } }}>
          {tabValue === 0 ? "HIGH" : "LOW"}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ flex: 1, textAlign: 'right', pr: 2, fontSize: '0.7rem', display: { xs: 'none', sm: 'block' } }}>LTP</Typography>
        <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ flex: { xs: 1, sm: 1.5 }, textAlign: 'right', fontSize: '0.7rem' }}>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>LTP/Change</Box>
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Change</Box>
        </Typography>
      </Box>

      <Box>
        {displayData.length > 0 ? (
          <Stack spacing={0}>
            {displayData.map((stock, idx) => (
              <StockRow key={idx} stock={stock} type={tabValue === 0 ? 'high' : 'low'} />
            ))}
          </Stack>
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.disabled">No data available</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default YearlyHighLowWidget;
